import { useEffect, useState } from 'react';
import {
  Cpu,
  ChevronDown,
  Loader2,
  AlertCircle,
  CheckCircle2,
  ExternalLink,
  Trash2,
  Save,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { domainApi } from '@/lib/api';

interface AgentSurfacesSettingsProps {
  domainId: string;
  isVerified: boolean;
}

interface SupportedSpec {
  path: string;
  label: string;
  contentType: 'application/json' | 'text/plain';
}

interface StoredFile {
  path: string;
  contentType: string;
  body: string;
  enabled: boolean;
  updatedAt: string;
  publishedUrl: string;
}

// Minimal starter templates per spec. Customers replace placeholders with
// their own values. Empty quotes mean "fill this in"; arrays/objects show
// the expected shape.
const TEMPLATES: Record<string, string> = {
  '/.well-known/agent.json': JSON.stringify(
    {
      name: '',
      description: '',
      url: '',
      version: '0.1.0',
      capabilities: [],
    },
    null,
    2
  ),
  '/.well-known/agents.json': JSON.stringify(
    {
      version: '0.1.0',
      description: '',
      flows: [],
    },
    null,
    2
  ),
  '/.well-known/agents.txt':
    '# agents.txt\n# Points agents at the other /.well-known/* files this site publishes.\n\nA2A: /.well-known/agent.json\nMCP: /.well-known/mcp/server-card.json\n',
  '/.well-known/ai-agent.json': JSON.stringify(
    { name: '', description: '', identity: {} },
    null,
    2
  ),
  '/.well-known/ai-plugin.json': JSON.stringify(
    {
      schema_version: 'v1',
      name_for_human: '',
      name_for_model: '',
      description_for_human: '',
      description_for_model: '',
      auth: { type: 'none' },
      api: { type: 'openapi', url: '' },
    },
    null,
    2
  ),
  '/.well-known/mcp/server-card.json': JSON.stringify(
    {
      name: '',
      description: '',
      mcpServerUrl: '',
      version: '0.1.0',
      capabilities: { tools: true, resources: true, prompts: false },
    },
    null,
    2
  ),
  '/.well-known/oauth-protected-resource': JSON.stringify(
    {
      resource: '',
      authorization_servers: [],
    },
    null,
    2
  ),
};

export function AgentSurfacesSettings({ domainId, isVerified }: AgentSurfacesSettingsProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [supported, setSupported] = useState<SupportedSpec[]>([]);
  const [files, setFiles] = useState<Record<string, StoredFile>>({});
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [openPath, setOpenPath] = useState<string | null>(null);
  const [savingPath, setSavingPath] = useState<string | null>(null);
  const [deletingPath, setDeletingPath] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const load = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await domainApi.listWellKnown(domainId);
      setSupported(res.supported);
      const next: Record<string, StoredFile> = {};
      for (const f of res.files) next[f.path] = f;
      setFiles(next);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load agent surfaces');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (isVerified && isExpanded) {
      load();
    } else {
      setIsLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainId, isVerified, isExpanded]);

  const getBody = (path: string) =>
    drafts[path] ?? files[path]?.body ?? TEMPLATES[path] ?? '';

  const setBody = (path: string, body: string) => {
    setDrafts((d) => ({ ...d, [path]: body }));
    setSuccess(null);
  };

  const handleSave = async (path: string) => {
    const body = getBody(path);
    setSavingPath(path);
    setError(null);
    setSuccess(null);
    try {
      const res = await domainApi.saveWellKnown(domainId, { path, body });
      setFiles((f) => ({
        ...f,
        [path]: {
          path,
          contentType: res.file.contentType,
          body: res.file.body,
          enabled: res.file.enabled,
          updatedAt: new Date().toISOString(),
          publishedUrl: res.file.publishedUrl,
        },
      }));
      setDrafts((d) => {
        const copy = { ...d };
        delete copy[path];
        return copy;
      });
      setSuccess(`Saved ${path}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSavingPath(null);
    }
  };

  const handleDelete = async (path: string) => {
    if (!confirm(`Disable ${path}? The file will return 404 until you re-save it.`)) return;
    setDeletingPath(path);
    setError(null);
    setSuccess(null);
    try {
      await domainApi.deleteWellKnown(domainId, path);
      setFiles((f) => {
        const copy = { ...f };
        delete copy[path];
        return copy;
      });
      setDrafts((d) => {
        const copy = { ...d };
        delete copy[path];
        return copy;
      });
      setSuccess(`Disabled ${path}.`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Delete failed');
    } finally {
      setDeletingPath(null);
    }
  };

  if (!isVerified) return null;

  return (
    <div className="bg-white rounded-xl border shadow-sm">
      <button
        type="button"
        onClick={() => setIsExpanded((v) => !v)}
        className="w-full flex items-center justify-between p-4 sm:p-6 text-left"
      >
        <div className="flex items-center gap-3">
          <Cpu className="w-5 h-5 text-slate-500" />
          <div>
            <h2 className="text-base sm:text-lg font-semibold">Agent surfaces</h2>
            <p className="text-xs sm:text-sm text-slate-500 mt-0.5">
              Publish MCP, A2A, and other <code className="font-mono">/.well-known/*</code> discovery files for AI agents.
            </p>
          </div>
        </div>
        <ChevronDown
          className={cn('w-5 h-5 text-slate-400 transition-transform', isExpanded && 'rotate-180')}
        />
      </button>

      {isExpanded && (
        <div className="border-t p-4 sm:p-6">
          {isLoading ? (
            <div className="flex items-center gap-2 text-slate-500 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" /> Loading…
            </div>
          ) : (
            <>
              {error && (
                <div className="flex items-start gap-2 p-3 mb-4 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">
                  <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
              {success && (
                <div className="flex items-start gap-2 p-3 mb-4 bg-green-50 border border-green-200 rounded-md text-sm text-green-700">
                  <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{success}</span>
                </div>
              )}

              <div className="text-xs text-slate-500 mb-4">
                Each file publishes at your CNAME subdomain immediately. Pair with the apex proxy snippet above to mirror them at your apex domain — where agents actually look.
              </div>

              <div className="space-y-2">
                {supported.map((spec) => {
                  const stored = files[spec.path];
                  const isOpen = openPath === spec.path;
                  const draftDirty = drafts[spec.path] !== undefined;
                  return (
                    <div key={spec.path} className="border border-slate-200 rounded-md">
                      <button
                        type="button"
                        onClick={() => setOpenPath(isOpen ? null : spec.path)}
                        className="w-full flex items-center justify-between p-3 text-left hover:bg-slate-50"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-sm">{spec.label}</div>
                          <div className="font-mono text-xs text-slate-500 truncate">{spec.path}</div>
                        </div>
                        <div className="flex items-center gap-2 ml-3">
                          {stored ? (
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-green-100 text-green-700 rounded-full">
                              Published
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded-full">
                              Not published
                            </span>
                          )}
                          <ChevronDown
                            className={cn(
                              'w-4 h-4 text-slate-400 transition-transform',
                              isOpen && 'rotate-180'
                            )}
                          />
                        </div>
                      </button>

                      {isOpen && (
                        <div className="border-t border-slate-200 p-3 space-y-2">
                          <textarea
                            value={getBody(spec.path)}
                            onChange={(e) => setBody(spec.path, e.target.value)}
                            spellCheck={false}
                            className="w-full h-56 px-3 py-2 border border-slate-200 rounded-md font-mono text-xs focus:outline-none focus:ring-2 focus:ring-indigo-200"
                            placeholder={
                              spec.contentType === 'application/json'
                                ? '{ "name": "..." }'
                                : 'plain text content'
                            }
                          />
                          <div className="flex flex-wrap gap-2">
                            <button
                              type="button"
                              onClick={() => handleSave(spec.path)}
                              disabled={savingPath === spec.path}
                              className="inline-flex items-center gap-1 px-3 py-1.5 bg-indigo-600 text-white rounded-md text-sm font-medium hover:bg-indigo-700 disabled:opacity-50"
                            >
                              {savingPath === spec.path ? (
                                <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              ) : (
                                <Save className="w-3.5 h-3.5" />
                              )}
                              {draftDirty || !stored ? 'Save' : 'Re-save'}
                            </button>
                            {stored && (
                              <>
                                <a
                                  href={stored.publishedUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 px-3 py-1.5 border border-slate-300 rounded-md text-sm font-medium hover:bg-slate-50"
                                >
                                  Open published
                                  <ExternalLink className="w-3 h-3" />
                                </a>
                                <button
                                  type="button"
                                  onClick={() => handleDelete(spec.path)}
                                  disabled={deletingPath === spec.path}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 border border-red-200 text-red-700 rounded-md text-sm font-medium hover:bg-red-50 disabled:opacity-50"
                                >
                                  {deletingPath === spec.path ? (
                                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                  ) : (
                                    <Trash2 className="w-3.5 h-3.5" />
                                  )}
                                  Disable
                                </button>
                                <span className="inline-flex items-center text-xs text-slate-500 px-2">
                                  Updated {new Date(stored.updatedAt).toLocaleString()}
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
